/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Wizard Product Offerings Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.30.2
 * @since 1.10.0
 */

import { FC, Fragment, useCallback } from 'react';

import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import Title from '@components/common/Title/Title';
import { EBasketType } from '@modules/app/appConstants';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectTranslationWizardProductOfferings } from '@modules/internationalization/intSelectors';
import { IWizardProductOfferingsTranslation } from '@modules/internationalization/types';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import Wizard from '@modules/wizard/components/Wizard';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { TWizardStepNames } from '@modules/wizard/wizardTypes';
import { EProductsPageFrom, EProductsTab } from '@pages/PageProducts/tariffsAndServicesConstants';
import {
    makeSelectProductsCategoryIds,
    makeSelectProductsCategorySelected,
    makeSelectProductsItemsSelected,
    makeSelectProductsMeta,
    makeSelectProductsPageFrom,
    makeSelectProductsRegionSelected,
    makeSelectProductsTabSelected,
    makeSelectProductsTariffSelected,
    makeSelectProductsTariffSelectedRange,
} from '@pages/PageProducts/tariffsAndServicesSelectors';
import {
    IProductCategoryIds,
    IProductsMeta,
    IProductsRegionItem,
    IProductsSelected,
} from '@pages/PageProducts/tariffsAndServicesTypes';
import { IRootReducer } from '@reducers/rootReducer';
import { IRouteParams } from '@routes/constants/routesConstants';

import { productOfferingsHandleNextAction } from '../wizardProductOfferingsActions';
import { EWizardProductOfferingsSteps } from '../wizardProductOfferingsConstants';
import {
    makeSelectProductOfferingsIsStepValid,
    makeSelectProductOfferingsValidateError,
} from '../wizardProductOfferingsSelectors';
import { IWizardProductOfferingsStartData } from '../wizardProductOfferingsTypes';
import ProductOfferingsHeader from './ProductOfferingsHeader';
import ProductOfferingsStepConfirmation from './ProductOfferingsStepConfirmation';
import StepSelectDate from './StepSelectDate';
import { StepSelectMegaPowers } from './StepSelectMegaPowers';
import StepSelectSubscribers from './StepSelectSubscribers';

interface IWizardProductOfferingsProps {
    basketType: EBasketType;
}

export type TWizardProductOfferingsProps = IWizardProductOfferingsProps & IConnectedProps & IDispatchProps;

const ProductOfferings: FC<TWizardProductOfferingsProps> = (props) => {
    const {
        categorySelected,
        productsItemSelected,
        productsMeta,
        productsCategoryIds,
        productTariffSelected,
        productTariffRangeIdSelected,
        regionSelected,
        stepName,
        tabSelected,
        validateError,
        pageFrom,
    } = props;
    const { clientId } = useParams<IRouteParams>();

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardProductOfferingsSteps.StepSelectSubscribers:
                return <StepSelectSubscribers />;
            case EWizardProductOfferingsSteps.StepSelectMegaPowers:
                return <StepSelectMegaPowers />;
            case EWizardProductOfferingsSteps.StepSelectDate:
                return <StepSelectDate />;
            case EWizardProductOfferingsSteps.StepConfirmation:
                return <ProductOfferingsStepConfirmation />;
        }
    }, [stepName]);

    /**
     * Load data to start wizard from Products Component
     */
    const getStartData = useCallback((): IWizardProductOfferingsStartData => {
        const data: IWizardProductOfferingsStartData = {
            isDisconnect: tabSelected === EProductsTab.CONNECTED,
            regionId: regionSelected.value,
        };

        if (pageFrom === EProductsPageFrom.SUBSCRIBER_ID_CHANGE_MEGA_POWERS) {
            data.onlyMegaPowers = true;
        }

        if (
            categorySelected === productsMeta.tariffsCategoryId ||
            productsCategoryIds.tariffsCartegoryIds.includes(categorySelected)
        ) {
            // подключение публичного тарифа
            data.ratePlan = { productOfferingId: productTariffRangeIdSelected || productTariffSelected.id };
        } else if (
            categorySelected === productsMeta.campaignTariffsCategoryId ||
            productsCategoryIds.campaignTariffsCartegoryIds.includes(categorySelected)
        ) {
            // подключение непубличного тарифа
            data.ratePlan = {
                productOfferingId: productTariffSelected.id,
                personalOfferingId: productTariffSelected.personalOfferingId,
                limitConnections: productTariffSelected?.limitConnections,
                connected: productTariffSelected?.connected,
            };
        } else if (categorySelected === productsMeta.campaignServicesCategoryId) {
            // подключение непубличной услуги
            data.services = productsItemSelected.map((item) => ({
                personalOfferingId: item.personalOfferingId,
                productOfferingId: item.id,
                limitConnections: item?.limitConnections,
                connected: item?.connected,
            }));
        } else {
            // подключение публичной услуги
            data.services = productsItemSelected.map((item) => ({
                productOfferingId: item.id,
            }));
        }
        return data;
    }, [
        productTariffSelected?.connected,
        productTariffSelected?.limitConnections,
        tabSelected,
        regionSelected.value,
        pageFrom,
        categorySelected,
        productsMeta.tariffsCategoryId,
        productsMeta.campaignTariffsCategoryId,
        productsMeta.campaignServicesCategoryId,
        productsCategoryIds.tariffsCartegoryIds,
        productsCategoryIds.campaignTariffsCartegoryIds,
        productTariffRangeIdSelected,
        productTariffSelected.id,
        productTariffSelected.personalOfferingId,
        productsItemSelected,
    ]);

    return (
        <Fragment>
            <PageLocale
                lazy
                fileNames={[
                    TranslationFileNamesEnum.common,
                    TranslationFileNamesEnum.corporate,
                    TranslationFileNamesEnum.wizardProductOfferings,
                    TranslationFileNamesEnum.wizard,
                ]}
            />

            <Title pageName={ECommonTranslationPages.productOfferings} />

            <Wizard
                afterStepsComponent={<ProductOfferingsHeader />}
                basketType={props.basketType}
                entityId={+clientId}
                getStartData={getStartData}
                isNextDisabled={!props.isStepValid}
                name={
                    props.basketType === EBasketType.CAMPAIGN_PARTICIPANTS
                        ? EWizardName.PRODUCT_OFFERINGS_CAMPAIGN
                        : EWizardName.PRODUCT_OFFERINGS
                }
                skipRestore={props.basketType === EBasketType.CAMPAIGN_PARTICIPANTS}
                translationProps={{
                    controls: props.translationProductOfferings.controls,
                    error: props.translationProductOfferings.error,
                    steps: props.translationProductOfferings.steps,
                    title: props.translationProductOfferings.title,
                }}
                validateError={validateError}
                onWizardNextHandler={props.handleNext}
            >
                {renderStep()}
            </Wizard>
        </Fragment>
    );
};

interface IConnectedProps {
    categorySelected: number;
    isStepValid: boolean;
    productsItemSelected: IProductsSelected[];
    productsMeta: IProductsMeta;
    productsCategoryIds: IProductCategoryIds;
    productTariffSelected: IProductsSelected;
    productTariffRangeIdSelected: number;
    regionSelected: IProductsRegionItem;
    stepName: TWizardStepNames;
    tabSelected: EProductsTab;
    translationProductOfferings: IWizardProductOfferingsTranslation;
    validateError: string;
    pageFrom: EProductsPageFrom;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    categorySelected: makeSelectProductsCategorySelected(),
    isStepValid: makeSelectProductOfferingsIsStepValid(),
    productsItemSelected: makeSelectProductsItemsSelected(),
    productsMeta: makeSelectProductsMeta(),
    productsCategoryIds: makeSelectProductsCategoryIds(),
    productTariffSelected: makeSelectProductsTariffSelected(),
    productTariffRangeIdSelected: makeSelectProductsTariffSelectedRange(),
    regionSelected: makeSelectProductsRegionSelected(),
    stepName: makeSelectWizardStepName(),
    tabSelected: makeSelectProductsTabSelected(),
    translationProductOfferings: makeSelectTranslationWizardProductOfferings(),
    validateError: makeSelectProductOfferingsValidateError(),
    pageFrom: makeSelectProductsPageFrom(),
});

export interface IDispatchProps {
    handleNext: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(productOfferingsHandleNextAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductOfferings);
